/**
 * アプリ全体を包む Error Boundary。
 *
 * - 子コンポーネントの render / lifecycle で投げられた例外を捕捉し、
 *   真っ白なページではなく日本語のエラーメッセージを表示する。
 * - 「Webアプリを起動しても白いページしか出ない」という不具合は、
 *   多くの場合 React の描画エラーが Error Boundary 不在のためルート要素ごと
 *   アンマウントされるのが原因。本コンポーネントで最低限のエラー表示を担保する。
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 開発時に原因追跡できるよう console に詳細を出力。
    console.error('Unhandled error in React tree:', error, info);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            padding: 24,
            color: '#b00020',
            fontFamily: 'sans-serif',
            maxWidth: 720,
            margin: '40px auto',
          }}
        >
          <h1>アプリの読み込み中にエラーが発生しました</h1>
          <p>{this.state.error?.message ?? '不明なエラー'}</p>
          <p>
            ブラウザのコンソールに詳細が出力されています。設定ファイル (web-app/public/config.json)
            や認証情報をご確認のうえ、ページを再読み込みしてください。
          </p>
          <button type="button" onClick={this.handleReload}>
            ページを再読み込みする
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
